/**
 * Message Bubble Component
 * Displays a single message (text or voice) with its feedback surface
 * (pronunciation and/or analysis signals) when the message carries them
 * Supports user bubbles (teal gradient, right-aligned) and AI bubbles (white, left-aligned)
 */

import React, { useCallback, useMemo } from 'react';
import { TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import {
  Box,
  VStack,
  HStack,
  Text,
} from '@gluestack-ui/themed';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { colors, FS_XS, FS_SM } from '../../theme';
import { AnalyzedMessage, LessonReference } from '../../types/conversation';
import { FeedbackCard, FeedbackBadge } from './FeedbackCard';
import type { PracticeErrorParams } from './FeedbackCard';
import { AnalysisErrorBadge } from './AnalysisErrorBadge';
import { AnalysisPendingBadge } from './AnalysisPendingBadge';
import QuestionCard from './QuestionCard';
import type { ErrorInfo } from '../../types/conversation';
import { useConversationStore } from '../../store/conversationStore';
import { useTTSStore } from '../../store/ttsStore';
import { useStreamingTTSStore } from '../../store/streamingTTSStore';
import { getAudioMode } from '../../hooks/useAudioMode';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import {
  parseNarrationSegments,
  segmentsFromNarrateDirective,
  shouldSegment as segmentsShouldRender,
  spokenDialogue,
  type NarrationSegment,
} from './narrationSegments';

/**
 * Subscribe to streaming text only for the actively streaming message.
 * Returns undefined for non-streaming messages (stable primitive, zero overhead).
 * React.memo prevents parent-triggered re-renders; this internal Zustand
 * selector triggers re-renders only for the one streaming MessageBubble.
 */
function useStreamingText(messageId: string, isStreaming: boolean): string | undefined {
  return useConversationStore((s) => {
    if (!isStreaming || s.streamingMessageId !== messageId) return undefined;
    return s.streamingText;
  });
}

const BASE_MESSAGE_SIZE = 15;
const BASE_MESSAGE_LINE_HEIGHT = 22;
const BASE_TIMESTAMP_SIZE = 11;

interface MessageBubbleProps {
  message: AnalyzedMessage;
  onToggleExpand: (messageId: string) => void;
  onRetry: (messageId: string) => void;
  onRetryConversation: (messageId: string) => void;
  onRetryAnalysis: (messageId: string) => void;
  onReportAI?: (messageId: string) => void;
  onLessonPress?: (lesson: LessonReference) => void;
  onPracticePress?: (params: PracticeErrorParams) => void;
  onSelectOption?: (optionText: string) => void;
}

function formatTime(date: Date): string {
  return new Date(date).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface StatusIndicatorProps {
  status: string;
  errorInfo?: ErrorInfo;
  onRetry?: () => void;
  isUserBubble?: boolean;
}

const StatusIndicator = React.memo(function StatusIndicator({ status, errorInfo, onRetry, isUserBubble }: StatusIndicatorProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  switch (status) {
    case 'queued':
      return (
        <HStack space="xs" alignItems="center">
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: isUserBubble ? 'rgba(255,255,255,0.7)' : theme.textSecondary, ...ffs }}
          >
            {t('chat.connection.queued')}
          </Text>
        </HStack>
      );
    case 'sending':
      return (
        <HStack space="xs" alignItems="center">
          <ActivityIndicator size="small" color={isUserBubble ? 'rgba(255,255,255,0.6)' : '#95A5A6'} />
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: isUserBubble ? 'rgba(255,255,255,0.7)' : theme.textSecondary, ...ffs }}
          >
            {t('chat.messages.sendingStatus')}
          </Text>
        </HStack>
      );
    case 'analyzing':
      return null; // Handled inline with timestamp now
    case 'error': {
      const errorMessage = errorInfo?.message || t('chat.messages.analysisFailed');
      const canRetry = errorInfo?.retryable !== false;

      return (
        <TouchableOpacity
          testID="chat-button-retry"
          accessibilityLabel={t('chat.messages.retryTooltip')}
          accessibilityRole="button"
          onPress={canRetry ? onRetry : undefined}
          activeOpacity={canRetry ? 0.7 : 1}
          disabled={!canRetry}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Box
            style={{ backgroundColor: theme.errorBg, borderColor: theme.error }}
            px="$3"
            py="$2"
            rounded="$lg"
            borderWidth={1}
          >
            <VStack space="xs">
              <HStack space="xs" alignItems="center">
                <Text allowFontScaling={false} accessible={false} importantForAccessibility="no" style={{ fontSize: FS_SM * fontScale }}>⚠️</Text>
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: theme.error, fontWeight: '600', ...ffs }}
                >
                  {errorMessage}
                </Text>
              </HStack>
              {canRetry && (
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: theme.error, textAlign: 'center', fontStyle: 'italic', ...ffs }}
                >
                  {t('chat.messages.retryText')}
                </Text>
              )}
            </VStack>
          </Box>
        </TouchableOpacity>
      );
    }
    default:
      return null;
  }
});

const TTS_ERROR_KEYS: Set<string> = new Set([
  'chat.tts.connectionError',
  'chat.tts.serviceError',
  'chat.tts.rateLimited',
  'chat.tts.playbackError',
]);

/** Translate a TTS error key using literal strings (required by literal-i18n-keys rule) */
function translateTTSError(t: TFunction, key: string): string {
  switch (key) {
    case 'chat.tts.connectionError': return t('chat.tts.connectionError');
    case 'chat.tts.serviceError': return t('chat.tts.serviceError');
    case 'chat.tts.rateLimited': return t('chat.tts.rateLimited');
    case 'chat.tts.playbackError': return t('chat.tts.playbackError');
    default: return key;
  }
}

/**
 * TTS speaker button for AI messages.
 * Subscribes to useTTSStore via selector — only the playing message re-renders.
 * Shows inline error text below the button when TTS fails.
 */
const TTSButton = React.memo(function TTSButton({ messageId, messageText, isStreamingTTS }: { messageId: string; messageText: string; isStreamingTTS: boolean }) {
  const { t } = useTranslation();
  const { theme, fontScale } = useDisplayTheme();
  const isPlaying = useTTSStore((s) => s.playingMessageId === messageId);
  const speak = useTTSStore((s) => s.speak);
  // Cost attribution (ADR-026): the tts store must not read the chat store.
  const sessionId = useConversationStore((s) => s.currentSessionId);
  const ttsErrorKey = useTTSStore((s) =>
    s.ttsError?.messageId === messageId ? s.ttsError.i18nKey : null,
  );
  const cancelStreamingTTS = useStreamingTTSStore((s) => s.cancel);

  const isActive = isPlaying || isStreamingTTS;

  const handlePress = useCallback(() => {
    if (isStreamingTTS) {
      cancelStreamingTTS();
      return;
    }
    speak(messageId, messageText, sessionId ?? undefined);
  }, [isStreamingTTS, cancelStreamingTTS, speak, messageId, messageText, sessionId]);

  const hasKnownError = ttsErrorKey != null && TTS_ERROR_KEYS.has(ttsErrorKey);

  return (
    <VStack alignItems="flex-end">
      <TouchableOpacity
        testID={`chat-tts-${messageId}`}
        accessibilityLabel={isActive ? t('chat.messages.stopListening') : t('chat.messages.listenToMessage')}
        accessibilityRole="button"
        onPress={handlePress}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        style={ttsStyles.button}
      >
        <Text
          allowFontScaling={false}
          style={{ fontSize: 14 * fontScale, color: isActive ? theme.primary : hasKnownError ? theme.error : theme.textSecondary }}
        >
          {isActive ? '\u{23F9}\u{FE0E}' : '\u{1F50A}'}
        </Text>
      </TouchableOpacity>
      {hasKnownError && ttsErrorKey && (
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.error, marginTop: 2 }}
        >
          {translateTTSError(t, ttsErrorKey)}
        </Text>
      )}
    </VStack>
  );
});

/**
 * Guard wrapper: controls TTS button visibility during streaming lifecycle.
 *
 * - Streaming TTS active (starting/active/finishing): stop button (⏹)
 * - Text available, no active streaming TTS: speaker button (🔊)
 * - No text yet: hidden
 *
 * The speaker button appears as soon as text is available, even during
 * text streaming. This avoids coupling to the backend 'done' event which
 * may be delayed by analysis (parallel mode).
 */
const TTSButtonGuard = React.memo(function TTSButtonGuard({
  messageId, messageText,
}: {
  messageId: string; messageText: string;
}) {
  const audioMode = getAudioMode();
  const isStreamingTTS = useStreamingTTSStore((s) =>
    s.activeMessageId === messageId && (s.status === 'starting' || s.status === 'active' || s.status === 'finishing'),
  );

  // Hide TTS button when audio is muted
  if (audioMode === 'mute') return null;

  return <TTSButton messageId={messageId} messageText={messageText} isStreamingTTS={isStreamingTTS} />;
});

const ttsStyles = StyleSheet.create({
  button: {
    padding: 4,
  },
});

/**
 * Renders an assistant turn that contains narrator markup as an ordered
 * stack of distinct blocks: narration as a centered, italic "stage
 * direction" (no bubble), dialogue as the normal white AI bubble with an
 * optional secondary-character label. Used only when the turn actually
 * contains a `Narrator:` line; ordinary turns render plainly (see below).
 */
const NarratedMessageBody = React.memo(function NarratedMessageBody({
  messageId,
  segments,
  fontSize,
  lineHeight,
  fontFamilyStyle,
  textColor,
  mutedColor,
  cardColor,
  borderColor,
  accentColor,
  cueBg,
  narrationLabel,
  cueLabel,
}: {
  messageId: string;
  segments: NarrationSegment[];
  fontSize: number;
  lineHeight: number;
  fontFamilyStyle?: { fontFamily: string };
  textColor: string;
  mutedColor: string;
  cardColor: string;
  borderColor: string;
  accentColor: string;
  cueBg: string;
  narrationLabel: string;
  cueLabel: string;
}) {
  return (
    <VStack space="sm">
      {segments.map((seg, i) => {
        if (seg.kind === 'narration') {
          return (
            <Box
              key={`n-${i}`}
              testID={`chat-message-narration-${messageId}-${i}`}
              accessibilityRole="text"
              accessibilityLabel={`${narrationLabel}: ${seg.text}`}
              px="$3"
            >
              <Text
                allowFontScaling={false}
                style={{
                  fontSize,
                  lineHeight,
                  color: mutedColor,
                  fontStyle: 'italic',
                  textAlign: 'center',
                  ...fontFamilyStyle,
                }}
              >
                {seg.text}
              </Text>
            </Box>
          );
        }
        if (seg.kind === 'cue') {
          // The forward instruction — a distinct soft-accent callout so the
          // learner sees exactly what to say next, set apart from the scene.
          return (
            <Box
              key={`c-${i}`}
              testID={`chat-message-cue-${messageId}-${i}`}
              accessibilityRole="text"
              accessibilityLabel={`${cueLabel}: ${seg.text}`}
              px="$4"
              py="$3"
              style={{ backgroundColor: cueBg, borderRadius: 14 }}
            >
              <Text
                allowFontScaling={false}
                style={{
                  fontSize,
                  lineHeight,
                  color: accentColor,
                  fontWeight: '600',
                  textAlign: 'center',
                  ...fontFamilyStyle,
                }}
              >
                {seg.text}
              </Text>
            </Box>
          );
        }
        return (
          <Box
            key={`d-${i}`}
            testID={`chat-message-dialogue-${messageId}-${i}`}
            p="$4"
            borderWidth={1}
            style={{
              backgroundColor: cardColor,
              borderColor,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              borderBottomRightRadius: 20,
              borderBottomLeftRadius: 4,
            }}
          >
            {seg.speaker ? (
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: fontSize * 0.82,
                  fontWeight: '700',
                  color: mutedColor,
                  marginBottom: 2,
                  ...fontFamilyStyle,
                }}
              >
                {seg.speaker}
              </Text>
            ) : null}
            <Text
              allowFontScaling={false}
              style={{ fontSize, lineHeight, color: textColor, ...fontFamilyStyle }}
            >
              {seg.text}
            </Text>
          </Box>
        );
      })}
    </VStack>
  );
});

export const MessageBubble = React.memo(function MessageBubble({ message, onToggleExpand, onRetry, onRetryConversation, onRetryAnalysis, onReportAI, onLessonPress, onPracticePress, onSelectOption }: MessageBubbleProps) {
  const { theme, fontScale, fontFamily } = useDisplayTheme();
  const { t } = useTranslation();

  const msgFontSize = BASE_MESSAGE_SIZE * fontScale;
  const msgLineHeight = Math.ceil(BASE_MESSAGE_LINE_HEIGHT * fontScale);
  const tsFontSize = BASE_TIMESTAMP_SIZE * fontScale;
  const fontFamilyStyle = fontFamily ? { fontFamily } : undefined;

  // Stable callbacks — message.id is stable for a given MessageBubble instance
  const handleRetry = useCallback(() => onRetry(message.id), [onRetry, message.id]);
  const handleRetryConversation = useCallback(() => onRetryConversation(message.id), [onRetryConversation, message.id]);
  const handleRetryAnalysis = useCallback(() => onRetryAnalysis(message.id), [onRetryAnalysis, message.id]);
  const handleToggle = useCallback(() => onToggleExpand(message.id), [onToggleExpand, message.id]);

  const isVoice = message.type === 'voice';
  const isStreaming = message.status === 'streaming';
  const streamingText = useStreamingText(message.id, isStreaming);
  const messageText = isVoice ? message.transcription : (streamingText ?? message.text);
  // Narrator-forward turns: prefer the structured directives — `narrate`
  // (Pattern A: scene + cue), `narrate_reply` (Pattern B: scene + speaker +
  // cue), or `narrate_react` (Pattern C: reaction + scene + cue). Fall back to
  // legacy label-parsing for older/streaming turns that still carry inline
  // "Narrator:" / "[Name]:" markers. Ordinary turns yield no narration and
  // render plainly. Cheap; only the streaming bubble re-runs.
  const narrateDirectives =
    !isVoice && message.type === 'text' ? message.directives : undefined;
  const narrate = narrateDirectives?.narrate;
  const narrateReply = narrateDirectives?.narrate_reply;
  const narrateReact = narrateDirectives?.narrate_react;
  const hasNarrateDirective = !!narrate || !!narrateReply || !!narrateReact;
  const segments = useMemo(
    () =>
      isVoice
        ? []
        : hasNarrateDirective
          ? segmentsFromNarrateDirective(messageText, {
              narrate,
              narrate_reply: narrateReply,
              narrate_react: narrateReact,
            })
          : parseNarrationSegments(messageText),
    [isVoice, hasNarrateDirective, narrate, narrateReply, narrateReact, messageText],
  );
  const isNarrated = segmentsShouldRender(segments);
  // For a structured narrator turn, read the whole beat aloud in the rendered
  // order: Pattern C leads with the reaction (reaction → scene → cue); A and B
  // lead with the scene (scene → [speaker] → cue). For the legacy label path,
  // TTS reads only the dialogue (never stage directions).
  const ttsText = hasNarrateDirective
    ? (narrateReact
        ? [narrateReact.reaction_line?.trim(), messageText.trim(), narrateReact.cue?.trim()]
        : [messageText.trim(), narrateReply?.speaker_line?.trim(), (narrateReply ?? narrate)?.cue?.trim()]
      )
        .filter(Boolean)
        .join('\n')
    : isNarrated
      ? spokenDialogue(segments, messageText)
      : messageText;
  const hasAnalysis = message.analysis !== undefined;
  const hasAnalysisError = !hasAnalysis && message.analysisError !== undefined;
  const isAnalysisPending = message.analysisPending === true && !hasAnalysis && !hasAnalysisError;
  // Pronunciation feedback rides every voice turn in every flow — including
  // inline flows that never run an analysis pass (CONV-015). Score 0 means the
  // SDK returned no real assessment. The analysis lifecycle badges keep
  // precedence so analysis-flow sequencing (pending → badge / error) is
  // unchanged.
  const pronunciationResult = isVoice ? message.pronunciationResult : undefined;
  const hasPronunciationFeedback =
    pronunciationResult !== undefined && pronunciationResult.pronunciationScore > 0;
  const showFeedback =
    hasAnalysis || (hasPronunciationFeedback && !isAnalysisPending && !hasAnalysisError);
  const isExpanded = message.isAnalysisExpanded ?? false;
  const isError = message.status === 'error';
  const isSending = message.status === 'sending' || message.status === 'queued';
  const isUser = (message.role ?? 'user') === 'user';

  if (isUser) {
    // User message: right-aligned, teal gradient
    return (
      <Box testID={`chat-message-user-${message.id}`} mb="$4" px="$4" alignSelf="flex-end" width="82%">
        {/* Message Bubble with gradient */}
        <LinearGradient
          colors={[colors.userBubbleStart, colors.userBubbleEnd]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderBottomRightRadius: 4,
            borderBottomLeftRadius: 20,
            padding: 16,
          }}
        >
          <VStack space="sm">
            {/* Message text */}
            <Text
              allowFontScaling={false}
              style={{
                fontSize: msgFontSize,
                lineHeight: msgLineHeight,
                color: 'white',
                ...fontFamilyStyle,
              }}
            >
              {messageText}
            </Text>

            {/* Error state with retry */}
            {isError && (
              <Box mt="$1">
                <StatusIndicator
                  status={message.status}
                  errorInfo={message.errorInfo}
                  onRetry={handleRetry}
                  isUserBubble
                />
              </Box>
            )}
          </VStack>
        </LinearGradient>

        {/* Timestamp, aligned right */}
        <Box mt="$2" alignItems="flex-end">
          <Text
            allowFontScaling={false}
            style={{ fontSize: tsFontSize, color: theme.textSecondary }}
          >
            {formatTime(message.createdAt)}
          </Text>
        </Box>

        {/* Feedback badge or card below bubble, aligned right */}
        {showFeedback && (
          <Box mt="$3" alignItems="flex-end">
            <TouchableOpacity
              testID={`chat-message-expand-${message.id}`}
              accessibilityLabel={isExpanded ? t('chat.feedback.collapseLabel') : t('chat.feedback.expandLabel')}
              accessibilityRole="button"
              accessibilityState={{ expanded: isExpanded }}
              onPress={handleToggle}
              activeOpacity={0.7}
            >
              {isExpanded ? (
                <FeedbackCard
                  analysis={message.analysis}
                  pronunciationResult={pronunciationResult}
                  isExpanded={true}
                  messageId={message.id}
                  onReport={onReportAI}
                  onLessonPress={onLessonPress}
                  onPracticePress={onPracticePress}
                />
              ) : (
                <FeedbackBadge
                  analysis={message.analysis}
                  pronunciationResult={pronunciationResult}
                  onPress={handleToggle}
                />
              )}
            </TouchableOpacity>
          </Box>
        )}

        {/* Analysis Pending Badge — shown while analysis runs in background */}
        {isAnalysisPending && (
          <Box mt="$3" alignItems="flex-end">
            <AnalysisPendingBadge />
          </Box>
        )}

        {/* Analysis Error Badge — shown when analysis failed but message was delivered */}
        {hasAnalysisError && (
          <Box mt="$3" alignItems="flex-end">
            <AnalysisErrorBadge onRetry={handleRetryAnalysis} />
          </Box>
        )}
      </Box>
    );
  }

  // AI/assistant message: left-aligned, white background
  return (
    <Box testID={`chat-message-${message.id}`} mb="$4" px="$4" alignSelf="flex-start" width="82%">
      {/* Message Bubble — hidden when text is empty and directives are present */}
      {(messageText || isStreaming || !(message.type === 'text' && message.directives)) && (
        isNarrated ? (
          /* Narrator turn: distinct stage-direction blocks + dialogue bubbles */
          <VStack space="sm">
            <NarratedMessageBody
              messageId={message.id}
              segments={segments}
              fontSize={msgFontSize}
              lineHeight={msgLineHeight}
              fontFamilyStyle={fontFamilyStyle}
              textColor={theme.text}
              mutedColor={theme.textSecondary}
              cardColor={theme.card}
              borderColor={theme.border}
              accentColor={theme.primary700}
              cueBg={theme.primarySoft}
              narrationLabel={t('chat.messages.narration')}
              cueLabel={t('chat.messages.cue')}
            />
            {isError && (
              <Box mt="$1">
                <StatusIndicator
                  status={message.status}
                  errorInfo={message.errorInfo}
                  onRetry={handleRetryConversation}
                />
              </Box>
            )}
          </VStack>
        ) : (
      <Box
        p="$4"
        borderWidth={1}
        style={{
          backgroundColor: theme.card,
          borderColor: theme.border,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          borderBottomRightRadius: 20,
          borderBottomLeftRadius: 4,
        }}
      >
        <VStack space="sm">
          {/* Message text (or empty placeholder during streaming start) */}
          {(messageText || !isStreaming) ? (
            <Text
              allowFontScaling={false}
              style={{
                fontSize: msgFontSize,
                lineHeight: msgLineHeight,
                color: theme.text,
                ...fontFamilyStyle,
              }}
            >
              {messageText}
            </Text>
          ) : null}

          {/* Streaming typing indicator — only before first token arrives */}
          {isStreaming && !messageText && (
            <HStack space="xs" alignItems="center">
              <ActivityIndicator size="small" color={colors.primary} />
            </HStack>
          )}

          {/* Status indicators */}
          {isSending && (
            <Box mt="$1">
              <StatusIndicator status={message.status} />
            </Box>
          )}

          {isError && (
            <Box mt="$1">
              <StatusIndicator
                status={message.status}
                errorInfo={message.errorInfo}
                onRetry={handleRetryConversation}
              />
            </Box>
          )}
        </VStack>
      </Box>
        )
      )}


      {/* Question card with option buttons (from AI directive) */}
      {!isStreaming && message.type === 'text' && message.directives?.question && onSelectOption && (
        <QuestionCard
          question={message.directives.question}
          onSelectOption={onSelectOption}
        />
      )}
      {/* Timestamp + TTS button, aligned left */}
      <HStack mt="$2" alignItems="center" justifyContent="space-between">
        <Text
          allowFontScaling={false}
          style={{ fontSize: tsFontSize, color: theme.textSecondary }}
        >
          {formatTime(message.createdAt)}
        </Text>
        {ttsText ? (
          <TTSButtonGuard messageId={message.id} messageText={ttsText} />
        ) : null}
      </HStack>
    </Box>
  );
});
